import React, { lazy, Suspense, useContext } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { LoadingSpinner } from 'cprime-foundational-components';
import { Roles } from 'context/Roles.context';

const Reports = lazy(() => import('./pages/Reports/Reports'));
const TestingTool = lazy(() => import('./pages/TestingTool/TestingTool'));
const AdminConsole = lazy(() => import('./pages/AdminConsole/AdminConsole'));
const GradeHierarchy = lazy(() => import('./pages/GradeHierarchy/GradeHierarchy'));
const BusinessRulesManager = lazy(() =>
  import('./pages/BusinessRulesManager/BusinessRulesManager'),
);
const VehicleSearchManagement = lazy(() =>
  import('./pages/VehicleSearchManagement/VehicleSearchManagement'),
);
const ErrorNotFound = lazy(() => import('./pages/ErrorNotFound/ErrorNotFound'));
const AccessDenied = lazy(() => import('./pages/AccessDenied/AccessDenied'));
const ExecutiveDashboard = lazy(() => import('./pages/ExecutiveDashboard/ExecutiveDashboard'));

const Router = () => {
  const auth = useAuth();
  const { permissions } = useContext(Roles);

  switch (auth.activeNavigator) {
    case 'signoutRedirect':
    case 'signinSilent':
    case 'signinRedirect':
      return <div>Redirecting to Login...</div>;
  }

  if (auth.isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (auth.error) {
    return <div>Oops... {auth.error.message}</div>;
  }

  if (!auth.isAuthenticated) {
    const pathname = window.location.pathname;
    if (pathname !== '/') {
      sessionStorage.setItem('cube-pathname', window.location.pathname);
    }
    auth.signinRedirect();
    return null;
  }

  // permissions are still in flight - render nothing routable until they resolve,
  // otherwise the access-denied gate below can be bypassed
  if (!permissions) {
    return <LoadingSpinner fullScreen />;
  }

  const getReportRoutes = () => {
    if (!Object.values(permissions?.reports?.tableau || {}).some((a) => a === true)) {
      return <></>;
    }

    return (
      <Route path="/reports" element={<Outlet />}>
        {!!permissions?.reports?.tableau?.analytics && (
          <Route
            path="usage-analytics"
            element={<Reports title="Usage Analytics" report="sfeanalytics" />}
          />
        )}
        {!!permissions?.reports?.tableau?.simulation && (
          <Route
            path="simulation-dashboard"
            element={<Reports title="Simulation Dashboard" report="sfedashboard" />}
          />
        )}
      </Route>
    );
  };

  const hasAnyPermission =
    permissions?.search?.view || Object.values(permissions?.reports?.tableau || {}).some(Boolean);

  // where "/" should land a user who has no search.view but does have a report
  const getLandingRoute = () => {
    if (permissions?.reports?.tableau?.analytics) {
      return '/reports/usage-analytics';
    }
    return '/reports/simulation-dashboard';
  };

  if (!hasAnyPermission) {
    return (
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner fullScreen />}>
          <AccessDenied />
        </Suspense>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Routes>
          {/* to be changed once we implement other pages */}
          <Route
            index
            path="/"
            element={
              permissions?.search?.view ? (
                <TestingTool />
              ) : (
                <Navigate to={getLandingRoute()} replace />
              )
            }
          />

          {!!permissions?.search?.view && (
            <Route index path="/testing-tool" element={<TestingTool />} />
          )}
          {!!permissions?.search?.view && (
            <Route index path="/admin-console" element={<AdminConsole />} />
          )}
          {!!permissions?.search?.view && (
            <Route index path="/grade-hierarchy" element={<GradeHierarchy />} />
          )}
          {!!permissions?.search?.view && (
            <Route index path="/category-and-match-group" element={<BusinessRulesManager />} />
          )}
          {!!permissions?.search?.view && (
            <Route index path="/vehicle-search-management" element={<VehicleSearchManagement />} />
          )}
          {getReportRoutes()}
          {!!permissions?.search?.view && (
            <Route path="/executive-dashboard" element={<ExecutiveDashboard />} />
          )}
          <Route path="*" element={<ErrorNotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default Router;
